/**
 * Pure functions for handling reducer cases for fully paid securities lending confirmations
 * These functions handle state transformations separately from routing logic
 */

/**
 * Extracts account ID from API request path
 * Handles both lowercase and uppercase UUID formats
 * @param {string|undefined} path - The API request path
 * @returns {string|null} - The account ID if found, null otherwise
 */
export const getAccountIdFromPath = (path) => {
	if (!path) return null;
	// Match UUID format (hex characters and hyphens) - case insensitive to handle both formats
	const match = path.match(/accounts\/([a-fA-F0-9-]+)\//);
	return match ? match[1] : null;
};

/**
 * Extracts account ID from action response or request path
 * @param {Object} action - The Redux action
 * @returns {string|null} - The account ID if found, null otherwise
 */
export const extractAccountId = (action) => {
	return action.response?.account_id || getAccountIdFromPath(action.request?.path);
};

/**
 * Validates that action response has required properties for success case
 * Uses extractAccountId to align with error handling fallback logic
 * @param {Object} action - The Redux action
 * @returns {boolean} - True if response is valid (has account_id or can extract from path), false otherwise
 */
export const isValidSuccessResponse = (action) => {
	const accountId = extractAccountId(action);
	return !!accountId;
};

/**
 * Normalizes account state to consistent { documents, error } shape
 * Handles migration from array format to normalized object format
 * @param {Array|Object|undefined} currentAccountState - Current state for the account
 * @returns {Object} - Normalized state object with documents array and error flag
 */
const normalizeAccountState = (currentAccountState) => {
	// Handle legacy array format (for backward compatibility)
	if (Array.isArray(currentAccountState)) {
		return {
			documents: currentAccountState,
			error: null,
		};
	}

	// Handle normalized object format
	if (currentAccountState && typeof currentAccountState === 'object') {
		return {
			documents: currentAccountState.documents || [],
			error: currentAccountState.error || null,
			// Preserve any other metadata
			...Object.keys(currentAccountState).reduce((acc, key) => {
				if (key !== 'documents' && key !== 'error') {
					acc[key] = currentAccountState[key];
				}
				return acc;
			}, {}),
		};
	}

	// Default empty state
	return {
		documents: [],
		error: null,
	};
};

/**
 * Handles the SUCCESS action case
 * Pure function that transforms state for successful confirmation fetch
 * Normalizes state to consistent { documents, error } shape
 * @param {Object} state - Current reducer state
 * @param {Object} action - The Redux action
 * @returns {Object} - New state with confirmations added in normalized format
 */
export const handleSuccessCase = (state, action) => {
	const accountId = extractAccountId(action);

	if (!accountId) {
		return state;
	}

	const priorState = normalizeAccountState(state[accountId]);
	const documents = action.response?.fpsl_confirms || [];

	return {
		...state,
		[accountId]: {
			...priorState,
			documents,
			error: null, // Clear any previous error on success
		},
	};
};

/**
 * Creates error state object for an account
 * Preserves existing confirmation data and normalizes to consistent { documents, error } shape
 * @param {Array|Object|undefined} currentAccountState - Current state for the account
 * @param {Object} action - The Redux action
 * @returns {Object} - Error state object with normalized shape
 */
export const createErrorState = (currentAccountState, action) => {
	const normalized = normalizeAccountState(currentAccountState);

	return {
		...normalized,
		error: action.response?.error || action.response || true,
	};
};

/**
 * Handles the ERRORED action case
 * Pure function that transforms state for failed confirmation fetch
 * @param {Object} state - Current reducer state
 * @param {Object} action - The Redux action
 * @returns {Object} - New state with error added, or unchanged state if account ID not found
 */
export const handleErroredCase = (state, action) => {
	const accountId = extractAccountId(action);

	if (!accountId) {
		return state;
	}

	return {
		...state,
		[accountId]: createErrorState(state[accountId], action),
	};
};
